import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { API, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { isValidCPF, isValidCNPJ, onlyDigits } from "../lib/br-doc";
import { processAndUploadImage } from "../lib/imageUpload";
import { isNative } from "../lib/native";
import { PhotoSourceSheet } from "../components/PhotoSourceSheet";
import { KycWhyLink } from "../components/KycWhyModal";
import {
  ArrowLeft, Camera, FloppyDisk, IdentificationCard,
  UploadSimple, CheckCircle, Warning, ClockClockwise, Clock, Trash, Plus, CircleNotch, Copy
} from "@phosphor-icons/react";

function maskCPF(v) {
  const d = v.replace(/\D/g, "").slice(0, 11);
  return d.replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}
function maskCNPJ(v) {
  const d = v.replace(/\D/g, "").slice(0, 14);
  return d
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
}
function maskPhone(v) {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d)/, "($1) $2-$3");
  return d.replace(/(\d{2})(\d{5})(\d)/, "($1) $2-$3");
}

export default function EditProfile() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  // V11.7: exclusão de conta
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const deleteAccount = async () => {
    if (deleteConfirm.trim().toUpperCase() !== "EXCLUIR") return;
    setDeleting(true);
    try {
      const { data } = await api.post("/me/delete-account", { reason: "" });
      toast.success(
        data?.hold_dispute
          ? "Conta desativada. A exclusão definitiva ocorrerá após a resolução da disputa em aberto."
          : "Conta desativada. Seus dados serão removidos em até 30 dias."
      );
      await logout();
      nav("/exclusao-de-conta");
    } catch (e) {
      toast.error(formatApiError(e) || "Não foi possível excluir a conta agora.");
      setDeleting(false);
    }
  };
  const isPJ = user?.doc_type === "cnpj";
  const [form, setForm] = useState({ name: "", email: "", phone: "", cpf: "", avatar_url: "", razao_social: "", nome_fantasia: "" });
  const [busy, setBusy] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [cnpjLoading, setCnpjLoading] = useState(false);
  // V7.7: refs para disparar click() programaticamente — funciona em qualquer webview mobile
  const avatarInputRef = useRef(null);
  // KYC form
  const [kyc, setKyc] = useState({ doc_type: "rg", doc_number: "", doc_front: "", doc_back: "" });
  const [kycBusy, setKycBusy] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState({ front: false, back: false });
  // V13.x: sheet nativo padronizado (Câmera / Galeria / Arquivo) para avatar e KYC
  const [avatarSheet, setAvatarSheet] = useState(false);
  const [kycSheet, setKycSheet] = useState(null); // "front" | "back" | null
  // V10.9: Horários de Atendimento (movido do anúncio para o perfil)
  const [atend, setAtend] = useState(null);
  const [savingAtend, setSavingAtend] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [searchParams] = useSearchParams();
  const atendRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    api.get("/me/atendimento")
      .then((r) => { setAtend(r.data); setSavedSnapshot(JSON.stringify(r.data.dias)); })
      .catch(() => { setAtend({ dias: {}, tz_label: "" }); setSavedSnapshot("{}"); });
  }, [user]);

  const dirtyAtend = atend && savedSnapshot !== null && JSON.stringify(atend.dias) !== savedSnapshot;

  // Epic 4: acesso em 1 clique (Perfil → Horários) rola direto até a seção
  useEffect(() => {
    if (searchParams.get("focus") === "horarios" && atend != null && atendRef.current) {
      const t = setTimeout(() => atendRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
      return () => clearTimeout(t);
    }
  }, [searchParams, atend]);

  const setDia = (k, patch) =>
    setAtend((s) => ({ ...s, dias: { ...s.dias, [k]: { ...(s.dias?.[k] || {}), ...patch } } }));

  const toggleDia = (k) => {
    const d = atend.dias?.[k] || {};
    if (d.aberto) setDia(k, { aberto: false });
    else setDia(k, { aberto: true, intervalos: d.intervalos?.length ? d.intervalos : [{ abre: "08:00", fecha: "18:00" }] });
  };

  const setIntv = (k, idx, patch) => {
    const arr = [...(atend.dias?.[k]?.intervalos || [])];
    arr[idx] = { ...arr[idx], ...patch };
    setDia(k, { intervalos: arr });
  };

  const addIntv = (k) => {
    const arr = [...(atend.dias?.[k]?.intervalos || [])];
    arr.push({ abre: "13:00", fecha: "18:00" });
    setDia(k, { intervalos: arr });
  };

  const removeIntv = (k, idx) => {
    const arr = (atend.dias?.[k]?.intervalos || []).filter((_, i) => i !== idx);
    setDia(k, { intervalos: arr, aberto: arr.length > 0 });
  };

  // Epic 4: preenchimento em lote — copia a Segunda (key "1") para Ter–Sex ("2".."5")
  const applyToWeekdays = () => {
    const src = atend?.dias?.["1"];
    if (!src || !src.aberto || !(src.intervalos || []).length) {
      toast.error("Configure a Segunda-feira (aberta, com horários) antes de aplicar.");
      return;
    }
    setAtend((s) => {
      const dias = { ...s.dias };
      ["2", "3", "4", "5"].forEach((k) => {
        dias[k] = { aberto: true, intervalos: (src.intervalos || []).map((iv) => ({ ...iv })) };
      });
      return { ...s, dias };
    });
    toast.success("Horário da Segunda aplicado de Terça a Sexta!");
  };

  const saveAtendimento = async () => {
    setSavingAtend(true);
    try {
      const { data } = await api.put("/me/atendimento", { dias: atend.dias });
      setAtend((s) => ({ ...s, ...data }));
      setSavedSnapshot(JSON.stringify(data.dias));
      toast.success("Horários de atendimento salvos!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingAtend(false);
    }
  };

  useEffect(() => {
    if (!user) {
      nav("/");
      return;
    }
    setForm({
      name: user.name || "",
      email: user.email || "",
      phone: maskPhone(user.phone || ""),
      cpf: isPJ ? maskCNPJ(user.cpf || "") : maskCPF(user.cpf || ""),
      avatar_url: user.avatar_url || "",
      razao_social: user.razao_social || "",
      nome_fantasia: user.nome_fantasia || "",
    });
    // Default doc type: PF→rg/cnh; PJ→cartao_cnpj
    const defaultType = isPJ ? "cartao_cnpj" : (user.kyc_doc_type || "rg");
    setKyc((k) => ({ ...k, doc_type: defaultType }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // V11.9 — pré-preenche o nº do documento (KYC) com o CPF/CNPJ já cadastrado
  useEffect(() => {
    if (!user?.cpf) return;
    const masked = isPJ ? maskCNPJ(user.cpf) : maskCPF(user.cpf);
    if (kyc.doc_type === "cartao_cnpj" || kyc.doc_type === "cpf") {
      setKyc((k) => ({ ...k, doc_number: masked }));
    } else if (kyc.doc_type === "rg" || kyc.doc_type === "cnh") {
      setKyc((k) => (k.doc_number === masked ? { ...k, doc_number: "" } : k));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kyc.doc_type, user]);

  const onUpload = async (file, kind) => {
    if (!file) return null;
    // Imagens são comprimidas/convertidas p/ webp automaticamente; PDFs passam direto.
    // V18.9.6 — kinds "kyc_front" e "kyc_back" enviam `?kind=kyc` para o backend
    // classificar o arquivo como PRIVADO (só dono/admin pode baixar).
    const isKyc = typeof kind === "string" && kind.startsWith("kyc");
    const data = await processAndUploadImage(file, isKyc ? { kind: "kyc" } : {});
    return data.path;
  };

  const handleAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    await uploadAvatarFile(f);
  };

  const uploadAvatarFile = async (f) => {
    if (!f) return;
    // Preview local imediato → evita piscar/voltar a foto atual durante o upload.
    const localUrl = URL.createObjectURL(f);
    setAvatarPreview(localUrl);
    setUploadingAvatar(true);
    try {
      const path = await onUpload(f, "avatar");
      setForm((s) => ({ ...s, avatar_url: path }));
      // Mantém o preview local exibido (não pisca enquanto o servidor serve a nova imagem).
    } catch (err) {
      setAvatarPreview(null);   // erro → volta para a foto atual
      toast.error("Falha no upload da foto");
    } finally {
      setUploadingAvatar(false);
    }
  };

  // No app nativo abre o menu Câmera/Galeria; no web usa o <input type="file">.
  const pickAvatar = async () => {
    if (uploadingAvatar) return;
    if (isNative()) { setAvatarSheet(true); return; }
    avatarInputRef.current?.click();
  };

  const save = async (e) => {
    e.preventDefault();
    const digits = onlyDigits(form.cpf);
    if (isPJ ? !isValidCNPJ(digits) : !isValidCPF(digits)) {
      toast.error(isPJ ? "CNPJ inválido. Confira os números." : "CPF inválido. Confira os números.");
      return;
    }
    setBusy(true);
    try {
      await api.patch("/auth/me", {
        name: form.name,
        email: form.email.toLowerCase().trim(),
        phone: form.phone.replace(/\D/g, ""),
        cpf: form.cpf.replace(/\D/g, ""),
        avatar_url: form.avatar_url,
        ...(isPJ ? { razao_social: form.razao_social, nome_fantasia: form.nome_fantasia } : {}),
      });
      await refresh?.();
      toast.success("Perfil atualizado");
      nav("/perfil");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // V12.0 — consulta CNPJ (Receita/BrasilAPI) e auto-preenche Razão Social / Nome Fantasia
  const fetchCnpj = async (digits) => {
    if (!isValidCNPJ(digits)) return;
    setCnpjLoading(true);
    try {
      const { data } = await api.get(`/cnpj/${digits}`);
      setForm((f) => ({
        ...f,
        razao_social: data.razao_social || f.razao_social,
        nome_fantasia: data.nome_fantasia || f.nome_fantasia,
      }));
      if (data.razao_social) toast.success("Dados da empresa atualizados pela Receita.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.info(typeof d === "string" ? d : "Não consegui buscar o CNPJ. Preencha os dados manualmente.");
    } finally {
      setCnpjLoading(false);
    }
  };

  const onCpfChange = (e) => {
    const masked = isPJ ? maskCNPJ(e.target.value) : maskCPF(e.target.value);
    setForm((s) => ({ ...s, cpf: masked }));
    if (isPJ && onlyDigits(masked).length === 14) fetchCnpj(onlyDigits(masked));
  };

  const doKycUpload = async (f, side) => {
    if (!f) return;
    setUploadingDoc((s) => ({ ...s, [side]: true }));
    try {
      const path = await onUpload(f, `kyc_${side}`);
      setKyc((k) => ({ ...k, [`doc_${side}`]: path }));
      toast.success(`Foto do ${side === "front" ? "frente" : "verso"} enviada`);
    } catch (err) {
      toast.error("Falha no upload do documento");
    } finally {
      setUploadingDoc((s) => ({ ...s, [side]: false }));
    }
  };

  const handleKycUpload = (e, side) => doKycUpload(e.target.files?.[0], side);

  // App nativo: abre o menu padronizado (Câmera/Galeria/Arquivo) ao invés do seletor do WebView.
  const captureKyc = (side) => setKycSheet(side);

  const submitKyc = async () => {
    if (!kyc.doc_number.trim()) return toast.warning("Informe o número do documento");
    if (!kyc.doc_front) return toast.warning("Envie a foto do documento");
    setKycBusy(true);
    try {
      await api.post("/auth/kyc/submit", kyc);
      await refresh?.();
      toast.success("Documento enviado! Aguardando aprovação do suporte.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setKycBusy(false);
    }
  };

  if (!user) return null;

  // V7.3 BUG FIX: aceitar `status=ativo_verificado` legado como approved,
  // alinhando com Profile.jsx (caso contrário tela "Editar" mostrava form de envio
  // mesmo após KYC aprovado).
  const kycStatus = user.kyc_status
    || (user.status === "ativo_verificado" ? "approved" : (user.doc_url ? "aguardando_validacao" : "pending"));
  const kycLabel = {
    approved: { text: "Identidade verificada", color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200", icon: CheckCircle },
    aguardando_validacao: { text: "Aguardando aprovação do cadastro", color: "text-amber-700", bg: "bg-amber-50 border-amber-200", icon: ClockClockwise },
    rejected: { text: "Documento rejeitado — reenvie", color: "text-red-700", bg: "bg-red-50 border-red-200", icon: Warning },
    pending: { text: "Validação de identidade pendente", color: "text-slate-700", bg: "bg-slate-50 border-slate-200", icon: IdentificationCard },
  }[kycStatus] || { text: "Validação pendente", color: "text-slate-700", bg: "bg-slate-50 border-slate-200", icon: IdentificationCard };
  const KycIcon = kycLabel.icon;
  const needsKyc = kycStatus !== "approved" && kycStatus !== "aguardando_validacao";
  const isWaiting = kycStatus === "aguardando_validacao";

  // Opções de documento filtradas por PF/PJ
  // V7.3: PF agora também aceita CPF (foto do CPF/Cartão CPF)
  const docTypeOptions = isPJ
    ? [{ v: "cartao_cnpj", label: "Cartão CNPJ" }]
    : [
        { v: "cpf", label: "CPF" },
        { v: "rg", label: "RG" },
        { v: "cnh", label: "CNH" },
      ];

  return (
    <div className="max-w-2xl mx-auto px-4 py-5 space-y-5" data-testid="edit-profile-page">
      <button onClick={() => nav("/perfil")} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#004E98]" data-testid="back-to-profile">
        <ArrowLeft size={14} weight="bold" /> Voltar
      </button>
      <header>
        <h1 className="font-heading font-black text-2xl">Editar Perfil</h1>
        <p className="text-sm text-slate-500">Atualize seus dados, foto e documentos.</p>
      </header>

      {/* Profile Form */}
      <form onSubmit={save} className="aj-card p-5 space-y-3">
        <div className="flex items-center gap-4">
          <div className="relative">
            {/* V7.7: input file global ref + tanto avatar quanto badge câmera disparam click() */}
            <input ref={avatarInputRef} type="file" accept="image/*" onChange={handleAvatar} disabled={uploadingAvatar} style={{ display: "none" }} data-testid="avatar-upload-input" />
            <button
              type="button"
              onClick={pickAvatar}
              disabled={uploadingAvatar}
              className="w-20 h-20 rounded-full bg-gradient-to-br from-[#004E98] to-[#FF6B35] text-white flex items-center justify-center font-heading text-2xl font-black overflow-hidden cursor-pointer disabled:opacity-50"
              data-testid="avatar-upload-trigger"
              aria-label="Alterar foto de perfil"
            >
              {avatarPreview || form.avatar_url ? (
                <img src={avatarPreview || `${API}/files/${form.avatar_url}`} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <span>{(isPJ ? (form.nome_fantasia || form.razao_social || form.name) : form.name)?.[0]?.toUpperCase() || "?"}</span>
              )}
            </button>
            {uploadingAvatar && (
              <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center" data-testid="avatar-uploading-overlay">
                <CircleNotch size={26} weight="bold" className="text-white animate-spin" />
              </div>
            )}
            <button
              type="button"
              onClick={pickAvatar}
              disabled={uploadingAvatar}
              className="absolute bottom-0 right-0 w-7 h-7 rounded-full bg-[#FF6B35] text-white flex items-center justify-center shadow ring-2 ring-white hover:bg-[#e25529] disabled:opacity-50"
              data-testid="avatar-upload-label"
              aria-label="Alterar foto de perfil"
            >
              <Camera size={14} weight="bold" />
            </button>
          </div>
          <div className="flex-1">
            <div className="font-bold text-lg">{(isPJ ? (form.nome_fantasia || form.razao_social || form.name || "Sua empresa") : (form.name || "Seu nome"))}</div>
            <div className="text-xs text-slate-500">{user.support_id}</div>
            {uploadingAvatar && <div className="text-[10px] text-slate-400 mt-1">Enviando foto...</div>}
          </div>
        </div>

        <label className="block">
          <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">{isPJ ? "Nome do responsável" : "Nome completo"}</span>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="aj-input mt-1" data-testid="edit-name" />
        </label>

        {isPJ && (
          <>
            <label className="block">
              <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Razão Social</span>
              <input value={form.razao_social} onChange={(e) => setForm({ ...form, razao_social: e.target.value })} className="aj-input mt-1" data-testid="edit-razao-social" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Nome Fantasia</span>
              <input value={form.nome_fantasia} onChange={(e) => setForm({ ...form, nome_fantasia: e.target.value })} className="aj-input mt-1" data-testid="edit-nome-fantasia" placeholder="Conforme o Cartão CNPJ (opcional)" />
              <span className="text-[10px] text-slate-400">É o nome que aparece no app. Se vazio, usamos a Razão Social.</span>
            </label>
          </>
        )}

        <label className="block">
          <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">E-mail</span>
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="aj-input mt-1" data-testid="edit-email" />
        </label>

        <label className="block">
          <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">WhatsApp</span>
          <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: maskPhone(e.target.value) })} className="aj-input mt-1" inputMode="tel" data-testid="edit-phone" />
        </label>

        <label className="block">
          <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">{isPJ ? "CNPJ" : "CPF"}</span>
          <input
            required
            value={form.cpf}
            onChange={onCpfChange}
            className="aj-input mt-1"
            inputMode="numeric"
            data-testid="edit-cpf"
          />
          {cnpjLoading && <span className="text-[11px] text-[#004E98]">Buscando dados do CNPJ na Receita…</span>}
          <span className="text-[10px] text-slate-400">Alterar o {isPJ ? "CNPJ" : "CPF"} requer nova validação de identidade.</span>
        </label>

        <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-50" data-testid="save-profile-btn">
          <FloppyDisk size={18} weight="bold" /> {busy ? "Salvando..." : "Salvar alterações"}
        </button>
      </form>

      {/* KYC */}
      <div className={`aj-card p-5 space-y-3 border-2 ${kycLabel.bg}`} data-testid="kyc-card">
        <div className="flex items-center gap-2">
          <KycIcon size={22} weight="duotone" className={kycLabel.color} />
          <h2 className="font-heading font-bold text-lg flex-1">Verificação de Identidade</h2>
        </div>
        <div className={`text-sm font-bold ${kycLabel.color}`} data-testid="kyc-status">{kycLabel.text}</div>

        {user.kyc_rejection_reason && kycStatus === "rejected" && (
          <div className="text-xs text-red-700 bg-red-100 p-2 rounded">
            Motivo: {user.kyc_rejection_reason}
          </div>
        )}

        {needsKyc ? (
          <div className="space-y-3 pt-2">
            <p className="text-xs text-slate-700">
              Para alugar ou anunciar, precisamos validar sua identidade. {isPJ ? "Envie uma foto do Cartão CNPJ da empresa." : "Envie uma foto do RG ou CNH."}
            </p>
            <KycWhyLink testId="kyc-why-profile" />

            <div className="flex gap-2 flex-wrap">
              {docTypeOptions.map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setKyc({ ...kyc, doc_type: opt.v })}
                  className={`flex-1 min-w-[100px] px-3 py-2 rounded-xl border-2 text-xs font-bold ${kyc.doc_type === opt.v ? "border-[#004E98] bg-[#004E98]/5" : "border-slate-200"}`}
                  data-testid={`kyc-type-${opt.v}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <label className="block">
              <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Número do documento</span>
              <input
                value={kyc.doc_number}
                onChange={(e) => setKyc({ ...kyc, doc_number: e.target.value })}
                placeholder={kyc.doc_type === "cartao_cnpj" ? "00.000.000/0000-00" : (kyc.doc_type === "cnh" ? "00000000000" : "00.000.000-0")}
                className="aj-input mt-1"
                data-testid="kyc-doc-number"
              />
            </label>

            <div className={`grid ${isPJ ? "grid-cols-1" : "grid-cols-2"} gap-2`}>
              <label className={`block border-2 border-dashed rounded-xl p-3 text-center cursor-pointer relative overflow-hidden ${kyc.doc_front ? "border-emerald-400 bg-emerald-50" : "border-slate-300 hover:border-[#004E98]"}`} data-testid="kyc-upload-front-label">
                {uploadingDoc.front ? (
                  <>
                    <UploadSimple size={22} className="mx-auto text-[#004E98] mb-1 animate-pulse" weight="bold" />
                    <div className="text-[11px] font-bold text-[#004E98]">Enviando...</div>
                  </>
                ) : kyc.doc_front ? (
                  <>
                    <CheckCircle size={22} weight="fill" className="mx-auto text-emerald-600 mb-1" />
                    <div className="text-[11px] font-bold text-emerald-700">Foto enviada ✓</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Toque para trocar</div>
                  </>
                ) : (
                  <>
                    <UploadSimple size={22} className="mx-auto text-slate-500 mb-1" weight="bold" />
                    <div className="text-[11px] font-bold">{isPJ ? "Foto do Cartão CNPJ" : "Frente do documento"}</div>
                  </>
                )}
                <input type="file" accept="image/*,application/pdf" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" onClick={(e) => { if (isNative()) { e.preventDefault(); captureKyc("front"); } }} onChange={(e) => handleKycUpload(e, "front")} data-testid="kyc-upload-front" disabled={uploadingDoc.front} />
              </label>

              {!isPJ && (
                <label className={`block border-2 border-dashed rounded-xl p-3 text-center cursor-pointer relative overflow-hidden ${kyc.doc_back ? "border-emerald-400 bg-emerald-50" : "border-slate-300 hover:border-[#004E98]"}`} data-testid="kyc-upload-back-label">
                  {uploadingDoc.back ? (
                    <>
                      <UploadSimple size={22} className="mx-auto text-[#004E98] mb-1 animate-pulse" weight="bold" />
                      <div className="text-[11px] font-bold text-[#004E98]">Enviando...</div>
                    </>
                  ) : kyc.doc_back ? (
                    <>
                      <CheckCircle size={22} weight="fill" className="mx-auto text-emerald-600 mb-1" />
                      <div className="text-[11px] font-bold text-emerald-700">Verso enviado ✓</div>
                    </>
                  ) : (
                    <>
                      <UploadSimple size={22} className="mx-auto text-slate-500 mb-1" weight="bold" />
                      <div className="text-[11px] font-bold">Verso (opcional)</div>
                    </>
                  )}
                  <input type="file" accept="image/*,application/pdf" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" onClick={(e) => { if (isNative()) { e.preventDefault(); captureKyc("back"); } }} onChange={(e) => handleKycUpload(e, "back")} data-testid="kyc-upload-back" disabled={uploadingDoc.back} />
                </label>
              )}
            </div>

            <button
              onClick={submitKyc}
              disabled={kycBusy || !kyc.doc_front || !kyc.doc_number || uploadingDoc.front || uploadingDoc.back}
              className="btn-primary w-full disabled:opacity-50"
              data-testid="submit-kyc-btn"
            >
              {kycBusy ? "Enviando..." : "Enviar para validação"}
            </button>
          </div>
        ) : isWaiting ? (
          <div className="pt-2 space-y-2" data-testid="kyc-waiting-section">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
              <b>Seu documento foi enviado.</b><br />
              Estamos analisando. Você receberá um e-mail quando for aprovado (até 24h).
              {user.kyc_doc_type && (
                <div className="mt-1.5 text-[11px]">
                  Tipo: <b>{(user.kyc_doc_type || "").toUpperCase().replace("_", " ")}</b>
                  {user.kyc_doc_number && <> · Nº <b>{user.kyc_doc_number}</b></>}
                </div>
              )}
            </div>
            <p className="text-[11px] text-slate-500">Caso precise corrigir algo, aguarde a rejeição do suporte para reenviar.</p>
          </div>
        ) : (
          <p className="text-xs text-slate-600 pt-1">Sua identidade está validada. Em caso de dúvidas, contate <a href="mailto:suporte@alugaja.app.br" className="underline">suporte@alugaja.app.br</a>.</p>
        )}
      </div>

      {/* V10.9: Horários de Atendimento (vale para todos os anúncios do dono) */}
      <div ref={atendRef} className="aj-card p-5 space-y-3" data-testid="atendimento-card">
        <div className="flex items-center gap-2">
          <Clock size={22} weight="duotone" className="text-[#004E98]" />
          <h2 className="font-heading font-bold text-lg flex-1">Horários de Atendimento</h2>
        </div>
        <p className="text-xs text-slate-600">
          Defina os dias e horários em que você entrega/recebe seus itens. Vale para <b>todos os seus anúncios</b> e limita os horários que o locatário pode escolher na reserva.
        </p>
        {atend?.tz_label && (
          <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1 inline-flex items-center gap-1" data-testid="atendimento-tz">
            🌎 Fuso da sua região{atend.uf ? ` (${atend.uf})` : ""}: <b>{atend.tz_label}</b>
          </div>
        )}
        {atend == null ? (
          <div className="text-sm text-slate-400">Carregando...</div>
        ) : (
          <div className="space-y-2">
            <button
              type="button"
              onClick={applyToWeekdays}
              className="w-full rounded-xl py-2 border-2 border-[#004E98]/30 bg-[#004E98]/5 text-[#004E98] text-xs font-bold flex items-center justify-center gap-1.5"
              data-testid="apply-weekdays-btn"
            >
              <Copy size={15} weight="bold" /> Aplicar a Segunda para todos os dias úteis (Ter–Sex)
            </button>
            {["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"].map((lbl, idx) => {
              const k = String(idx);
              const d = atend.dias?.[k] || { aberto: false, intervalos: [] };
              const intervalos = d.intervalos || [];
              return (
                <div key={k} className="flex items-start gap-2 py-1.5 border-b border-slate-100 last:border-0" data-testid={`atend-row-${idx}`}>
                  <button
                    type="button"
                    onClick={() => toggleDia(k)}
                    className={`w-24 shrink-0 text-left px-2.5 py-1.5 rounded-lg border-2 text-xs font-bold mt-0.5 ${d.aberto ? "border-[#004E98] bg-[#004E98]/5 text-[#004E98]" : "border-slate-200 text-slate-400"}`}
                    data-testid={`atend-toggle-${idx}`}
                  >
                    {lbl}
                  </button>
                  {!d.aberto ? (
                    <span className="text-xs text-slate-400 mt-2">Fechado</span>
                  ) : (
                    <div className="flex-1 space-y-1.5">
                      {intervalos.map((iv, j) => (
                        <div key={j} className="flex items-center gap-1.5" data-testid={`atend-intv-${idx}-${j}`}>
                          <input
                            type="time"
                            value={iv.abre || ""}
                            onChange={(e) => setIntv(k, j, { abre: e.target.value })}
                            className="aj-input py-1.5 px-2 w-[88px]"
                            data-testid={`atend-abre-${idx}-${j}`}
                          />
                          <span className="text-slate-400 text-xs">às</span>
                          <input
                            type="time"
                            value={iv.fecha || ""}
                            onChange={(e) => setIntv(k, j, { fecha: e.target.value })}
                            className="aj-input py-1.5 px-2 w-[88px]"
                            data-testid={`atend-fecha-${idx}-${j}`}
                          />
                          <button
                            type="button"
                            onClick={() => removeIntv(k, j)}
                            className="w-7 h-7 grid place-items-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50"
                            title="Remover horário"
                            data-testid={`atend-remove-${idx}-${j}`}
                          >
                            <Trash size={15} weight="bold" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addIntv(k)}
                        className="text-[11px] font-bold text-[#004E98] inline-flex items-center gap-1 hover:underline"
                        data-testid={`atend-add-${idx}`}
                      >
                        <Plus size={12} weight="bold" /> Adicionar horário
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            <p className="text-[11px] text-slate-400 pt-1">
              💡 Tem intervalo de almoço? Use "Adicionar horário" para deixar 2 períodos no mesmo dia (ex.: 08:00 às 12:00 e 13:30 às 18:00).
            </p>
            <button
              onClick={saveAtendimento}
              disabled={savingAtend || !dirtyAtend}
              className={`w-full mt-2 rounded-xl py-2.5 font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-100 ${
                dirtyAtend
                  ? "bg-[#FF6B35] text-white hover:bg-[#e85d2a]"
                  : "bg-emerald-100 text-emerald-700 border-2 border-emerald-300 cursor-default"
              }`}
              data-testid="save-atendimento-btn"
            >
              {savingAtend ? (
                "Salvando..."
              ) : dirtyAtend ? (
                <><FloppyDisk size={18} weight="bold" /> Salvar horários</>
              ) : (
                <><CheckCircle size={18} weight="fill" /> Horários salvos</>
              )}
            </button>
            {dirtyAtend && (
              <p className="text-[11px] text-amber-600 text-center font-bold" data-testid="atend-dirty-hint">
                Você tem alterações não salvas
              </p>
            )}
          </div>
        )}

        {/* V11.7: Excluir conta (LGPD / Google Play) */}
        <div className="aj-card p-5 border-2 border-red-200 bg-red-50/40" data-testid="danger-zone">
          <h2 className="font-bold text-red-700 text-base flex items-center gap-2">
            <Trash size={20} weight="bold" /> Excluir minha conta
          </h2>
          <p className="text-xs text-slate-600 mt-1.5">
            Ao excluir, sua conta é desativada imediatamente e seus dados pessoais são removidos
            após 30 dias. Registros financeiros são mantidos anonimizados por obrigação legal.{" "}
            <button type="button" onClick={() => nav("/exclusao-de-conta")} className="text-[#004E98] underline font-bold" data-testid="data-policy-link">
              Saiba como tratamos seus dados
            </button>.
          </p>
          <button
            type="button"
            onClick={() => { setShowDelete(true); setDeleteConfirm(""); }}
            className="mt-3 text-sm font-bold text-red-600 border-2 border-red-300 hover:bg-red-100 rounded-full py-2 px-4 inline-flex items-center gap-2"
            data-testid="open-delete-account"
          >
            <Trash size={16} weight="bold" /> Excluir minha conta
          </button>
        </div>
      </div>

      {showDelete && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="delete-account-modal">
          <div className="bg-white rounded-2xl p-5 w-full max-w-md space-y-3">
            <h3 className="font-heading font-black text-lg text-red-700 flex items-center gap-2">
              <Warning size={22} weight="fill" /> Confirmar exclusão de conta
            </h3>
            <p className="text-sm text-slate-600">
              Esta ação <b>desativa sua conta</b> e agenda a remoção dos seus dados pessoais em 30 dias.
              Seus anúncios sairão do ar. Para confirmar, digite <b>EXCLUIR</b> abaixo.
            </p>
            <input
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="Digite EXCLUIR"
              className="aj-input"
              data-testid="delete-confirm-input"
            />
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => setShowDelete(false)}
                className="flex-1 btn-ghost"
                data-testid="delete-cancel-btn"
              >
                Cancelar
              </button>
              <button
                onClick={deleteAccount}
                disabled={deleteConfirm.trim().toUpperCase() !== "EXCLUIR" || deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-bold rounded-full py-2.5 inline-flex items-center justify-center gap-2"
                data-testid="confirm-delete-account"
              >
                <Trash size={16} weight="bold" /> {deleting ? "Excluindo..." : "Excluir conta"}
              </button>
            </div>
          </div>
        </div>
      )}

      <PhotoSourceSheet
        open={avatarSheet}
        onClose={() => setAvatarSheet(false)}
        onPick={(files) => { if (files?.[0]) uploadAvatarFile(files[0]); }}
      />
      <PhotoSourceSheet
        open={!!kycSheet}
        allowFile
        onClose={() => setKycSheet(null)}
        onPick={(files) => { if (files?.[0]) doKycUpload(files[0], kycSheet); }}
      />
    </div>
  );
}
